import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { addBlobToCache, useLazyLoadedImage } from "@nova/toolkit-pure-hooks"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useRef, useCallback, useMemo, RefObject } from "react"
import { ProfilePhotoCropper, ProfilePhotoCropperHandle } from "./ProfilePhotoCropper"
import { selectUserID, selectUserProfilePhotoS3Key } from "user/profile/selectors"
import { ButtonTertiary, ButtonPrimary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import classNames from "classnames"
import { setProfilePhotoS3Key } from "user/profile/slice"
import { selectUserFullName } from "features/entity-caching/members/UserManagementSlice"
import { getColorFromId } from "@nova/util-strings"
import * as Icons from "@nova/component-icons"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { getProfilePhotoS3Key } from "@nova/util-s3"
import { IngestDataType } from "@nova/schemas"
import { SupportedRegions } from "@nova/constants"

function getFirstAndLastName(input: string): {
    firstName: string
    lastName?: string
} {
    const trimmedInput = input.trim()
    const words = trimmedInput.split(/\s+/)

    return {
        firstName: words[0],
        lastName: words.length >= 2 ? words.at(-1) : undefined,
    }
}

type SharedProps = {
    className?: string
    initialsFontSize?: number
    initialsPlacement?: "center" | "bottom-left"
    initialsFont?: "default" | "mono"
    objectPosition?: string
}

type SelfProps = {
    self: true
    allowUpload?: boolean
    userId?: never
    fullName?: never
}
type NotSelfProps = {
    self?: false
    allowUpload?: never
    userId: string
    fullName: string
}

export const ProfilePhoto = ({
    self,
    allowUpload = false,
    userId: userIdProp,
    fullName: fullNameProp,
    className,
    initialsFontSize = 50,
    initialsPlacement = "center",
    initialsFont = "default",
    objectPosition = "center",
}: SharedProps & (SelfProps | NotSelfProps)) => {
    const loggedInUserId = useAppSelector(selectUserID)
    const userId = useMemo(
        () => (self ? loggedInUserId : userIdProp),
        [loggedInUserId, self, userIdProp]
    )
    const loggedInUserS3Key = useAppSelector(selectUserProfilePhotoS3Key)
    const fetchBlob = useCallback(async () => {
        let s3Key: string | undefined
        if (self) {
            s3Key = loggedInUserS3Key
        } else {
            if (!userIdProp) return
            const { profilePhotoS3Key } = await fetchApiGateway("get /user/:userId/profile-photo", {
                params: {
                    userId: userIdProp,
                },
            })
            s3Key = profilePhotoS3Key
        }
        if (!s3Key) return
        const res = await fileManagementApi.fetchFile(s3Key, { region: SupportedRegions.Canada })
        const blob = await res.blob()
        return blob
    }, [loggedInUserS3Key, self, userIdProp])
    const { containerRef, blobUrl, isError } = useLazyLoadedImage({
        type: "ProfilePicture",
        imageKey: `${userId}`,
        fetchBlob,
    })
    const loggedInUserFullName = useAppSelector(selectUserFullName())
    const fullName = useMemo(
        () => (self ? loggedInUserFullName : fullNameProp),
        [fullNameProp, loggedInUserFullName, self]
    )
    const firstLast = fullName ? getFirstAndLastName(fullName) : undefined
    const last = firstLast?.lastName
    const initials = firstLast && `${firstLast.firstName[0]}${last ? last[0] : ""}`.toUpperCase()
    const textPlacement = useMemo(() => {
        switch (initialsPlacement) {
            case "bottom-left":
                return { x: "15%", y: "85%", anchor: "start", baseline: "auto" } as const
            default:
                return { x: "50%", y: "50%", anchor: "middle", baseline: "central" } as const
        }
    }, [initialsPlacement])
    return (
        <div
            ref={containerRef}
            className={classNames(
                "w-full h-full relative flex justify-center items-center group",
                className,
                {
                    "cursor-pointer": allowUpload,
                }
            )}
        >
            <div
                className={classNames(
                    "absolute inset-0 flex justify-center items-center text-white-700 rounded-[inherit]",
                    {
                        "font-mono": initialsFont === "mono",
                    }
                )}
                style={{
                    background: getColorFromId(userId || ""),
                }}
            >
                {/* SVG trick to scale text with the size of the container */}
                <svg
                    viewBox="0 0 100 100"
                    className="w-full h-full"
                    preserveAspectRatio="xMidYMid meet"
                >
                    <text
                        x={textPlacement.x}
                        y={textPlacement.y}
                        dominantBaseline={textPlacement.baseline}
                        textAnchor={textPlacement.anchor}
                        fontSize={initialsFontSize}
                        fill="currentColor"
                    >
                        {initials}
                    </text>
                </svg>
            </div>
            {!isError && blobUrl && (
                <img
                    key={userId}
                    src={blobUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover rounded-[inherit]"
                    style={{ objectPosition }}
                />
            )}
            {allowUpload && (
                <>
                    <div className="absolute bottom-1 right-1 z-20 pointer-events-none">
                        <Icons.SmallIcon
                            Icon={Icons.AddAPhoto}
                            className="group-hover:-translate-y-1 transition-transform"
                            iconClassName="text-white"
                        />
                    </div>
                    <ProfilePhotoUploader />
                </>
            )}
        </div>
    )
}

const ProfilePhotoUploader = () => {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)!
    const confirmationModal = useConfirmationModalContext()
    const inputRef = useRef<HTMLInputElement>(null)
    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file) return
            const cropperRef: RefObject<ProfilePhotoCropperHandle | null> = { current: null }
            await confirmationModal.confirm({
                RenderComponent: ({ confirm, cancel, isPerformingAsyncConfirmCallback }) => (
                    <ConfirmNewProfilePhotoConfirmationModal
                        confirm={confirm}
                        cancel={cancel}
                        isPerformingAsyncConfirmCallback={isPerformingAsyncConfirmCallback}
                        file={file}
                        cropperRef={cropperRef}
                    />
                ),
                asyncConfirmCallback: async () => {
                    const croppedFile = await cropperRef.current!.getCroppedFile()
                    const key = getProfilePhotoS3Key({ userId, fileId: croppedFile.name })
                    await fileManagementApi.putFile(croppedFile, {
                        target: key,
                        dataType: IngestDataType.ProfilePhoto,
                        regionOverride: SupportedRegions.Canada,
                    })
                    await fetchApiGateway("patch /me/profile", {
                        body: {
                            profilePhotoS3Key: key,
                        },
                    })
                    addBlobToCache({
                        type: "ProfilePicture",
                        imageKey: `${userId}`,
                        imageBlob: croppedFile,
                    })
                    dispatch(setProfilePhotoS3Key(key))
                },
            })
        },
        [confirmationModal, dispatch, userId]
    )
    return (
        <div
            className="absolute inset-0"
            onClick={() => {
                inputRef.current?.click()
            }}
        >
            <input
                type="file"
                ref={inputRef}
                onChange={handleFileChange}
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
            />
        </div>
    )
}

const ConfirmNewProfilePhotoConfirmationModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback,
    file,
    cropperRef,
}: {
    confirm: () => void
    cancel: () => void
    isPerformingAsyncConfirmCallback: boolean
    file: File
    cropperRef: RefObject<ProfilePhotoCropperHandle | null>
}) => {
    return (
        <HighPriorityNovaModalContainer
            title="Confirm Profile Photo"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary
                        title="Cancel"
                        onClick={cancel}
                        disabled={isPerformingAsyncConfirmCallback}
                    />
                    <ButtonPrimary
                        title="Confirm"
                        onClick={confirm}
                        disabled={isPerformingAsyncConfirmCallback}
                        isLoading={isPerformingAsyncConfirmCallback}
                    />
                </div>
            }
            closeModal={cancel}
        >
            <div className="flex justify-center items-center p-4">
                <ProfilePhotoCropper ref={cropperRef} file={file} />
            </div>
        </HighPriorityNovaModalContainer>
    )
}
